import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Sector,
  SectorProps,
} from 'recharts'
import { useAppSelector } from '@/hooks/storeHooks/storehooks'
import { formatDefPayload, getFromStorage, saveToStorage } from '@/lib/utils'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  fetchCompletedOrders,
  fetchOrderInProgressStatus,
} from '@/services/apis/api'
import { toast } from 'sonner'
import GradiantSkelton from '@/components/atom/gradiantSkelton/gradiantSkelton'
import { DropDownCmp } from '@/components/atom/dropdown/dropDown'
import {
  FetchOrderInProgressStatusResponse,
  OverviewOrderProgressProps,
} from '@/services/types/api.types'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable'
import SortableTabCard from './sortableTabCard'
import { useNavigate } from 'react-router-dom'

const ORDERS_TAB_CARD_IDS = ['orders-progress', 'orders-completed']

const OrdersTab = ({
  isWidgetVisible,
}: {
  isWidgetVisible: (key: string) => boolean
}) => {
  const navigate = useNavigate()
  const defPayload = useAppSelector(state => state.menu.payloadDef)
  const essentialIds = useMemo(() => formatDefPayload(defPayload), [defPayload])
  const [orderCards, setOrderCards] = useState(() => {
    const saved = getFromStorage('service_orderCards')
    return Array.isArray(saved) ? saved : ORDERS_TAB_CARD_IDS
  })

  useEffect(() => {
    saveToStorage('service_orderCards', orderCards)
  }, [orderCards])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  )

  const [orderProgressData, setOrderProgressData] =
    useState<OverviewOrderProgressProps>({
      inProgress: 0,
      completed: 0,
      customerHold: 0,
      spectrotelHold: 0,
      total: 0,
    })
  const [isOrdersLoading, setIsOrdersLoading] = useState(false)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)

  const handleDrilldown = (key: (typeof allChartData)[number]) => {
    navigate(key.route, { state: { key } })
  }

  const getOrderProgressstatus = useCallback(async () => {
    setIsOrdersLoading(true)
    try {
      const res = (await fetchOrderInProgressStatus({
        ...essentialIds,
      })) as FetchOrderInProgressStatusResponse

      if (res.status === 200) {
        setOrderProgressData(res.data.data.metrics)
      } else {
        toast.error('Something Went Wrong')
      }
    } catch {
      toast.error('Failed to load order status')
    }
    setIsOrdersLoading(false)
  }, [essentialIds])

  const [areaData, setAreaData] = useState<
    { date: string; orderCount: number }[]
  >([])
  const [isAreaLoading, setIsAreaLoading] = useState(false)
  const [timeFilter, setTimeFilter] = useState('CURRENT_WEEK')

  const getCompletedOrders = useCallback(async () => {
    setIsAreaLoading(true)
    try {
      const res = (await fetchCompletedOrders({
        ...essentialIds,
        datetime: timeFilter,
      })) as {
        status: number
        data: { data: { graphData: { date: string; orderCount: number }[] } }
      }
      if (res.status === 200) {
        setAreaData(res.data.data.graphData)
      } else {
        toast.error('Failed to fetch completed orders')
      }
    } catch {
      toast.error('Something Went Wrong')
    }
    setIsAreaLoading(false)
  }, [essentialIds, timeFilter])

  useEffect(() => {
    if (
      defPayload.customerId ||
      defPayload.siteId.length > 0 ||
      defPayload.billingSubGroup
    ) {
      setTimeout(() => {
        getCompletedOrders()
        getOrderProgressstatus()
      }, 0)
    }
  }, [
    defPayload.customerId,
    defPayload.siteId.length,
    defPayload.billingSubGroup,
    getCompletedOrders,
    getOrderProgressstatus,
  ])

  const outerData = [
    {
      name: 'Completed',
      value: orderProgressData?.completed || 0,
      color: 'var(--success)',
      route: '/service-delivery/orders',
    },
    {
      name: 'In Progress',
      value: orderProgressData?.inProgress || 0,
      color: 'var(--warning)',
      route: '/service-delivery/orders',
    },
  ]

  const innerData = [
    {
      name: 'Spectrotel Hold',
      value: orderProgressData?.spectrotelHold || 0,
      color: 'var(--graphline-2)',
      route: '/service-delivery/orders',
    },
    {
      name: 'Customer Hold',
      value: orderProgressData?.customerHold || 0,
      color: 'var(--danger)',
      route: '/service-delivery/orders',
    },
  ]

  const allChartData = [...outerData, ...innerData]

  const total = orderProgressData?.total || 0

  const handleOrdersDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setOrderCards(items => {
        const oldIndex = items.indexOf(active.id as string)
        const newIndex = items.indexOf(over.id as string)

        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const renderOrderCard = (cardId: string) => {
    switch (cardId) {
      case 'orders-progress':
        if (!isWidgetVisible('orders_in_progress_status')) return null
        return (
          <SortableTabCard
            key={cardId}
            id={cardId}
            className='h-full md:col-span-5'
          >
            <div className='flex h-full w-full flex-col rounded-xl border border-border border-l-[4px] border-l-accent bg-primary p-4 shadow-sm'>
              <div className='mb-4 flex items-center justify-between'>
                <h3 className='text-kpi-heading font-semibold text-primary-foreground'>
                  Orders In Progress Status
                </h3>
                <div className='flex items-center gap-2'></div>
              </div>

              {isOrdersLoading ? (
                <Skelton2 />
              ) : (
                <div className='flex flex-col xl:flex-row flex-1 items-center justify-between gap-4'>
                  <div className='relative h-48 w-48 flex-shrink-0'>
                    <ResponsiveContainer width='100%' height='100%'>
                      <PieChart>
                        {/* OUTER RING */}
                        <Pie
                          data={outerData}
                          innerRadius={70}
                          outerRadius={95}
                          paddingAngle={2}
                          dataKey='value'
                          stroke='none'
                          activeShape={(props: SectorProps) => (
                            <Sector
                              {...props}
                              outerRadius={(props.outerRadius || 95) + 4}
                            />
                          )}
                          onMouseLeave={() => setActiveIndex(null)}
                        >
                          {outerData.map((entry, index) => (
                            <Cell
                              key={`outer-cell-${index}`}
                              fill={entry.color}
                              style={{
                                cursor: 'pointer',
                                transition: 'all 0.25s ease',
                                filter:
                                  activeIndex === null || activeIndex === index
                                    ? 'brightness(1)'
                                    : 'brightness(0.7)',
                              }}
                              opacity={
                                activeIndex === null || activeIndex === index
                                  ? 1
                                  : 0.35
                              }
                              onMouseEnter={() => setActiveIndex(index)}
                              onClick={() => handleDrilldown(entry)}
                            />
                          ))}
                        </Pie>

                        {/* INNER RING */}
                        <Pie
                          data={innerData}
                          innerRadius={45}
                          outerRadius={65}
                          paddingAngle={2}
                          dataKey='value'
                          stroke='none'
                          activeIndex={
                            activeIndex !== null &&
                            activeIndex >= outerData.length
                              ? activeIndex - outerData.length
                              : undefined
                          }
                          activeShape={(props: SectorProps) => (
                            <Sector
                              {...props}
                              outerRadius={(props.outerRadius || 65) + 4}
                            />
                          )}
                          onMouseLeave={() => setActiveIndex(null)}
                        >
                          {innerData.map((entry, index) => {
                            const actualIndex = outerData.length + index

                            return (
                              <Cell
                                key={`inner-cell-${index}`}
                                fill={entry.color}
                                style={{
                                  cursor: 'pointer',
                                  transition: 'all 0.25s ease',
                                  filter:
                                    activeIndex === null ||
                                    activeIndex === actualIndex
                                      ? 'brightness(1)'
                                      : 'brightness(0.7)',
                                }}
                                opacity={
                                  activeIndex === null ||
                                  activeIndex === actualIndex
                                    ? 1
                                    : 0.35
                                }
                                onMouseEnter={() => setActiveIndex(actualIndex)}
                                onClick={() => handleDrilldown(entry)}
                              />
                            )
                          })}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>

                    <div className='absolute inset-0 flex flex-col items-center justify-center pointer-events-none'>
                      <span className='text-kpi-secondary-value font-semibold'>
                        {total}
                      </span>
                      <span className='mt-1 text-kpi-supporting-1 font-semibold text-primary-foreground'>
                        Total
                      </span>
                    </div>
                  </div>

                  <div className='grid grid-cols-2 gap-3 w-full flex-1'>
                    {allChartData.map((item, i) => {
                      const isActive = activeIndex === i

                      return (
                        <button
                          key={i}
                          onMouseEnter={() => setActiveIndex(i)}
                          onMouseLeave={() => setActiveIndex(null)}
                          onClick={() => handleDrilldown(item)}
                          className={`flex flex-col rounded-lg border border-border p-2.5 bg-white/50 text-left transition-all duration-200 hover:shadow-md hover:-translate-y-[2px] ${
                            isActive
                              ? 'ring-2 ring-primary/20 scale-[1.02]'
                              : ''
                          }`}
                        >
                          <div className='flex items-start gap-1.5 mb-1.5'>
                            <div
                              className='h-2 w-2 rounded-full mt-1 flex-shrink-0'
                              style={{ backgroundColor: item.color }}
                            />

                            <span className='text-kpi-supporting-1 font-medium text-primary-foreground leading-tight'>
                              {item.name}
                            </span>
                          </div>

                          <span className='text-primary-button font-bold pl-3.5 text-primary-foreground'>
                            {item.value}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          </SortableTabCard>
        )
      case 'orders-completed':
        if (!isWidgetVisible('orders_completed_over_time')) return null
        return (
          <SortableTabCard
            key={cardId}
            id={cardId}
            className='h-full md:col-span-6'
          >
            <div className='flex h-full w-full flex-col rounded-xl border border-border border-l-[4px] border-l-accent bg-primary p-4 shadow-sm'>
              <div className='mb-4 flex items-center justify-between'>
                <h3 className='text-kpi-heading font-semibold text-primary-foreground'>
                  Orders Completed Over Time
                </h3>
                <div className='flex items-center gap-2'>
                  <DropDownCmp
                    node={{
                      fieldName: 'ordersCompletedDropdown',
                      dropdownValue: [
                        { label: 'Last Month', value: 'LAST_MONTH' },
                        { label: 'Last Week', value: 'LAST_WEEK' },
                        { label: 'Last 30 days', value: 'LAST_30_DAYS' },
                        { label: 'Last 7 days', value: 'LAST_7_DAYS' },
                        { label: 'Current Week', value: 'CURRENT_WEEK' },
                      ],
                      value: timeFilter,
                      childClass:
                        'h-8 text-kpi-supporting-1 bg-tertiary border-none rounded-full px-4',
                    }}
                    handleChange={(_, val) => setTimeFilter(val)}
                  />
                </div>
              </div>

              {isAreaLoading ? (
                <Skelton1 />
              ) : (
                <div className='h-56 w-full'>
                  <ResponsiveContainer width='100%' height='100%'>
                    <AreaChart
                      data={areaData}
                      margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient
                          id='colorOrders'
                          x1='0'
                          y1='0'
                          x2='0'
                          y2='1'
                        >
                          <stop
                            offset='5%'
                            stopColor='#3B82F6'
                            stopOpacity={0.3}
                          />
                          <stop
                            offset='95%'
                            stopColor='#3B82F6'
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray='3 3' vertical={false} />
                      <XAxis
                        dataKey='date'
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={value => (value ? value.slice(5) : '')}
                        tick={{ fontSize: 10, fill: '#6B7280' }}
                        dy={10}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 10, fill: '#6B7280' }}
                      />
                      <RechartsTooltip />
                      <Area
                        type='monotone'
                        dataKey='orderCount'
                        stroke='#3B82F6'
                        strokeWidth={2}
                        fillOpacity={1}
                        fill='url(#colorOrders)'
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}

              <div className='mt-2 flex items-center justify-center gap-2'>
                <div className='flex items-center gap-2 text-kpi-supporting-1 text-blue-500'>
                  <div className='h-2 w-2 rounded-full bg-[#3B82F6]' />
                  <span>Orders Completed</span>
                </div>
              </div>
            </div>
          </SortableTabCard>
        )
      default:
        return null
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleOrdersDragEnd}
    >
      <SortableContext items={orderCards} strategy={rectSortingStrategy}>
        <div className='grid grid-cols-1 gap-4 md:grid-cols-11'>
          {orderCards.map(renderOrderCard)}
        </div>
      </SortableContext>
    </DndContext>
  )
}

export default OrdersTab

const Skelton1 = () => (
  <div className='h-56 w-full rounded-xl bg-primary relative overflow-hidden'>
    {/* Grid */}
    <div className='absolute inset-0 flex flex-col justify-between py-4'>
      {[1, 2, 3, 4, 5].map(item => (
        <div key={item} className='border-t border-dashed border-[#D6E4F5]' />
      ))}
    </div>

    {/* Area Graph Shape */}
    <svg
      viewBox='0 0 1000 220'
      preserveAspectRatio='none'
      className='absolute inset-0 h-full w-full'
    >
      <defs>
        <linearGradient id='skeletonAreaGradient' x1='0' y1='0' x2='0' y2='1'>
          <stop offset='0%' stopColor='#60A5FA' stopOpacity='0.25' />
          <stop offset='100%' stopColor='#60A5FA' stopOpacity='0.02' />
        </linearGradient>
      </defs>

      {/* Filled Area */}
      <path
        d='
        M0,170
        C80,120 140,130 220,90
        C300,50 380,70 460,110
        C540,150 620,130 700,80
        C780,35 860,55 1000,20
        L1000,220
        L0,220
        Z
      '
        fill='url(#skeletonAreaGradient)'
      />

      {/* Line */}
      <path
        d='
        M0,170
        C80,120 140,130 220,90
        C300,50 380,70 460,110
        C540,150 620,130 700,80
        C780,35 860,55 1000,20
      '
        fill='none'
        stroke='#60A5FA'
        strokeWidth='4'
        strokeLinecap='round'
        className='animate-pulse'
      />
    </svg>

    {/* Shimmer */}
    <div className='absolute inset-0 overflow-hidden'>
      <div className='absolute inset-y-0 -left-[40%] w-[40%] animate-shimmer bg-gradient-to-r from-transparent via-white/20 to-transparent' />
    </div>

    {/* X Axis Labels */}
    <div className='absolute bottom-1 left-0 flex w-full justify-between px-2'>
      {[1, 2, 3, 4, 5, 6, 7].map(item => (
        <GradiantSkelton
          key={item}
          width='w-8'
          height='h-3'
          className='!rounded-sm'
        />
      ))}
    </div>
  </div>
)

const Skelton2 = () => (
  <div className='flex flex-col xl:flex-row flex-1 items-center justify-between gap-4'>
    {/* Donut Skeleton */}
    <div className='relative flex h-48 w-48 flex-shrink-0 items-center justify-center'>
      {/* Outer ring */}
      <GradiantSkelton
        width='w-48'
        height='h-48'
        className='rounded-full opacity-90'
      />

      {/* Ring separator */}
      <div className='absolute h-36 w-36 rounded-full border-[10px] border-primary opacity-90' />

      {/* Center hole */}
      <div className='absolute flex h-28 w-28 items-center justify-center rounded-full bg-primary'>
        <div className='flex flex-col items-center gap-2'>
          <GradiantSkelton width='w-12' height='h-8' className='rounded-md' />

          <GradiantSkelton width='w-10' height='h-3' className='rounded-md' />
        </div>
      </div>
    </div>

    {/* Side Metric Cards Skeleton */}
    <div className='grid w-full flex-1 grid-cols-2 gap-3'>
      {[1, 2, 3, 4].map(item => (
        <div
          key={item}
          className='flex flex-col rounded-lg border border-border bg-white/50 p-2.5'
        >
          {/* label row */}
          <div className='mb-2 flex items-center gap-2'>
            <GradiantSkelton
              width='w-2'
              height='h-2'
              className='rounded-full'
            />

            <GradiantSkelton width='w-20' height='h-3' className='rounded-md' />
          </div>

          {/* value */}
          <div className='pl-4'>
            <GradiantSkelton width='w-10' height='h-7' className='rounded-md' />
          </div>
        </div>
      ))}
    </div>
  </div>
)
